/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QPATResponse } from 'qp-ui-interface';
import { BehaviorSubject, EMPTY, from, Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { NewrelicService } from 'src/app/analytics/newrelic/service/newrelic.service';
import { environment } from '../../../../environments/environment';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { getXClientId } from '../../utils/deviceUtils';
import { saveXAuthToken } from '../../utils/tokenUtils';
import { ConfigService } from '../config/config.service';
import { DeviceHandlerService } from '../device-handler/device-handler.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { QPATService } from '../qpat/qpat.service';
import { SubscriptionHandlerService } from '../subscription-handler.service';
import {
  AdobeAuthorizeDecision,
  AdobeProfile,
  AuthorizedResourcesResponse,
  ClientRegistration,
  ClientToken,
  MVPDData,
  PreauthorizedResourcesResponse,
  ProfilesResponse,
  SessionsResponse,
  UserMetadata,
  UserResponse,
} from './adobe.interface';

declare global {
  interface Window {
    env: string;
  }
}

@Injectable()
export class AdobeService {
  // TVE SESSION INFO
  private clientToken: ClientToken;
  private clientRegistration: ClientRegistration;
  private authorizedEntitlements: string[];
  private userMetadata: UserMetadata;
  private mvpdHash: string;
  private userResponseDefault = {
    userId: '',
    expires: 0,
  };
  private userResponse: BehaviorSubject<UserResponse> = new BehaviorSubject<UserResponse>(this.userResponseDefault);
  private shortMediaToken: string;
  private refreshTokenTimeout: ReturnType<typeof setTimeout>;

  // ADOBE SERVICE
  private apiUrl: string;
  private requestorId: string;
  private softwareStatement: string;
  private preAuthorizedResources: string[] = ['univision', 'galavision', 'deportes'];

  // MVPD selected image
  private mvpdConfigListUrl: string;
  private mvpdImageSrc: string;

  private serviceName = 'AdobeService';

  private profileSelected: AdobeProfile;

  constructor(
    private http: HttpClient,
    private deviceHandler: DeviceHandlerService,
    private readonly configService: ConfigService,
    private localStorage: LocalStorageService,
    private qpatService: QPATService,
    private subscriptionHandlerService: SubscriptionHandlerService,
    private newrelic: NewrelicService
  ) {
    this.apiUrl = 'https://sp.auth.adobe.com';
    this.requestorId = this.configService.applicationConfig?.requestorId ?? 'univision';
    this.softwareStatement = this.configService.applicationConfig?.adobeSoftwareStatement ?? environment.adobe.adobeSoftwareStatement;
    this.mvpdConfigListUrl =
      this.configService.applicationConfig?.adobeConfigUrl ?? window.env === 'prod'
        ? 'https://data-store.api-cms.prd.univisionnow.com/content/urn/resource/catalog/mvpd'
        : 'https://data-store.unowcms-stag.quickplay.com/content/urn/resource/catalog/mvpd';
  }

  get mvpd(): string {
    return this.userMetadata?.data?.mvpd;
  }

  get userId(): string {
    return this.userMetadata?.data.userID;
  }

  get entitlementPackages(): string[] {
    return this.authorizedEntitlements;
  }

  get fullEntitlementPackages(): string[] {
    if (!this.authorizedEntitlements) return this.authorizedEntitlements;
    return this.authorizedEntitlements.map((e) => `urn:package:tve:${e}`);
  }

  get mediaToken(): string {
    return this.shortMediaToken;
  }

  get mvpdImg(): string {
    return this.mvpdImageSrc;
  }

  set profileSel(prof: AdobeProfile) {
    this.profileSelected = prof;
  }

  // Return device activation code after needed session set.
  public getDeviceCode(): Observable<SessionsResponse> {
    if (this.clientToken) {
      return this.getRegCode();
    }
    return this.setRegister().pipe(
      switchMap(() => {
        return this.getRegCode();
      }),
      catchError((error) => {
        console.error('Error:', error);
        return of(null as unknown as SessionsResponse); // Prevents errors from breaking the stream
      })
    );
  }

  // User for device activation code. Validate every x ms this call.
  public pollForProfiles(deviceCode: string): Observable<ProfilesResponse> {
    const authHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      'AP-Device-Identifier': `fingerprint ${this.deviceHandler.getXDeviceFingerprint(this.clientRegistration.client_id)}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http.get(`${this.apiUrl}/api/v2/univision/profiles/code/${deviceCode}`, {
      headers: authHeaders,
    }) as Observable<ProfilesResponse>;
  }

  // Get the needed user data info
  public getAllUserData(profToAuth: AdobeProfile): Observable<QPATResponse> {
    try {
      return this.getPreauthorize(profToAuth).pipe(
        tap((response) => (this.authorizedEntitlements = response.decisions.filter((res) => res.authorized).map((res) => res.resource))),
        catchError((error) => {
          console.error('getPreauthorize failed:', error);
          return of(null);
        }),
        switchMap(() => {
          return this.getMVPDsList();
        }),
        tap((result: MVPDData) => {
          this.profileSelected = profToAuth;
          const mvpdSelected = result.data.find((mvpd) => mvpd.mso_id === profToAuth?.attributes?.mvpd?.value);
          const { id, ia, mso_hash, lon } = mvpdSelected;
          if (lon?.length > 0) {
            const name = lon[0].n;
            this.newrelic.store.setTveProvider(name);
          }
          this.mvpdHash = mso_hash;
          this.mvpdImageSrc = `${this.configService.applicationConfig.imageResizeURL}/image/${id}/${
            ia && ia.length > 0 ? ia.find((ar) => ar.includes('16x9')) ?? '0-16x9' : '0-16x9'
          }.png?width=210`;
        }),
        switchMap(() => {
          return from(this.qpatService.getQPAT());
        }),
        tap((result: QPATResponse) => {
          saveXAuthToken(result, this.localStorage);
        })
      );
    } catch (e) {
      return EMPTY;
    }
  }

  /**
   * Both calls are needed to init the adobe service for logged in users
   *   Register the application using the software statement
   *   Get user access token
   *
   * @returns ClientToken
   */
  public setRegister(): Observable<ClientToken> {
    return this.register().pipe(
      tap((response) => {
        this.clientRegistration = response;
        this.localStorage.setItem(LocalStorageKeys.CLIENT_REGISTRATION, JSON.stringify(response));
      }),
      switchMap(() => {
        // Call getToken to get the token, and set it after completion
        return this.getToken();
      }),
      tap((tokenResponse) => {
        // Set the token after getToken() is done
        this.clientToken = tokenResponse;
        this.localStorage.setItem(LocalStorageKeys.PROFILE_TOKEN, JSON.stringify(tokenResponse));
        this.localStorage.setItem(LocalStorageKeys.PROFILE_TOKEN_EXP, this.calcExpDate(tokenResponse.expires_in, tokenResponse.created_at));
        this.startRefreshTokenTimer();
      })
    );
  }

  /**
   * Validate if user is still with valid token
   * Get all user data if still valid. Force logout if invalid.
   */
  public silentLogin(): Observable<unknown> {
    const currProfile = this.localStorage.getItem(LocalStorageKeys.ACTIVE_PROFILE)
      ? JSON.parse(this.localStorage.getItem(LocalStorageKeys.ACTIVE_PROFILE))
      : undefined;
    const currToken = this.localStorage.getItem(LocalStorageKeys.PROFILE_TOKEN)
      ? JSON.parse(this.localStorage.getItem(LocalStorageKeys.PROFILE_TOKEN))
      : undefined;

    if (currProfile && currToken) {
      this.profileSelected = currProfile;
      this.clientToken = currToken;
      let tokenHasExpired = false;

      // Validate token expiration
      const expTime = this.localStorage.getItem(LocalStorageKeys.PROFILE_TOKEN_EXP);
      if (expTime) {
        const nowTime = Date.now();
        // Token has expired
        if (nowTime - parseInt(expTime, 10) >= 0) {
          tokenHasExpired = true;
        }
      }

      // Get Registration Data
      this.clientRegistration = this.localStorage.getItem(LocalStorageKeys.CLIENT_REGISTRATION)
        ? JSON.parse(this.localStorage.getItem(LocalStorageKeys.CLIENT_REGISTRATION))
        : undefined;

      if (tokenHasExpired) {
        return this.setRegister().pipe(
          switchMap(() =>
            this.checkAuthn(this.profileSelected).pipe(
              switchMap((profilesResp) => {
                if (profilesResp.profiles && Object.keys(profilesResp.profiles).length === 0) {
                  // If this doesnt have profiles, means the session is not valid anymore
                  this.cleanUserVariables();
                  return of(null);
                }
                return this.getAllUserData(currProfile);
              }),
              catchError(() => {
                console.error('Authentication failed: 404 Not Found');
                this.cleanUserVariables();
                return of(null); // Fallback value
              })
            )
          )
        );
      } else {
        return this.getAllUserData(currProfile).pipe(
          catchError(() => {
            console.error('Authentication failed: 404 Not Found');
            this.cleanUserVariables();
            return of(null); // Fallback value
          })
        );
      }
    } else {
      // User is not logged in
      console.warn('No active session found');
      this.cleanUserVariables();
      return EMPTY;
    }
  }

  // Logout user session
  public logout(): Observable<unknown> {
    const queryParams = new URLSearchParams();
    queryParams.set('redirectUrl', 'https://univision-comcast-ui-cdn.api-cms.prd.univisionnow.com/');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      'AP-Device-Identifier': `fingerprint ${this.deviceHandler.getXDeviceFingerprint(this.clientRegistration.client_id)}`,
    });

    return this.http
      .get<unknown>(
        `${this.apiUrl}/api/v2/univision/logout/${this.profileSelected?.attributes?.mvpd?.value || ''}?${queryParams.toString()}`,
        { headers }
      )
      .pipe(
        tap(() => {
          this.authorizedEntitlements = undefined;
        })
      );
  }

  /**
   * Trigger setToken for short-lived media token
   * Return true if user is entitled
   *        false if package is not entitled
   */
  public userIsEntitled = async (resourcesIds: string[]): Promise<boolean> => {
    const cleanedResources = resourcesIds.map((resource) => resource.replace('urn:package:tve:', ''));
    for (const resource of cleanedResources) {
      // Obtain token with first package that is included in pre-authorized packages
      if (this.entitlementPackages?.includes(resource)) {
        await this.authorizePlayer(resource).toPromise();
        return true;
      }
    }
    return false;
  };

  // Check if user has valid session active
  public checkAuthn(prof?: AdobeProfile): Observable<ProfilesResponse> {
    const authHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      'AP-Device-Identifier': `fingerprint ${this.deviceHandler.getXDeviceFingerprint(this.clientRegistration.client_id)}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http.get(
      `${this.apiUrl}/api/v2/${this.requestorId}/profiles${prof?.attributes?.mvpd?.value ? '/' + prof?.attributes?.mvpd?.value : ''}`,
      {
        headers: authHeaders,
      }
    ) as Observable<ProfilesResponse>;
  }

  public cleanUserVariables(): void {
    this.userMetadata = undefined;
    this.authorizedEntitlements = [];
    this.shortMediaToken = undefined;
    this.userResponse.next(this.userResponseDefault);
    this.mvpdImageSrc = undefined;
    this.mvpdHash = undefined;
    this.stopRefreshTokenTimer();

    this.localStorage.removeItem(LocalStorageKeys.XAUTH_TOKEN);
    this.localStorage.removeItem(LocalStorageKeys.AUTHDATA);
    this.localStorage.removeItem(LocalStorageKeys.USER_ID);
    this.localStorage.removeItem(LocalStorageKeys.ACTIVE_PROFILE);
    this.localStorage.removeItem(LocalStorageKeys.PROFILE_TOKEN);
    this.localStorage.removeItem(LocalStorageKeys.PROFILE_TOKEN_EXP);
  }

  public getUserInfo(): {
    mvpdHash: string;
    userID: string;
  } {
    return {
      mvpdHash: this.mvpdHash,
      userID: this.userId,
    };
  }

  private getMVPDsList(): Observable<MVPDData> {
    return this.http.get<MVPDData>(`${this.mvpdConfigListUrl}?client=${getXClientId()}&st=published,draft&pageSize=50`);
  }

  private authorizePlayer(resource: string): Observable<unknown> {
    return this.checkAuthorize(resource).pipe(
      tap((response: AuthorizedResourcesResponse) => {
        let resSelected: AdobeAuthorizeDecision;
        // Select profile to authorize
        response.decisions?.forEach((decision) => {
          if (resource === decision.resource) {
            resSelected = decision;
          }
        });
        this.shortMediaToken = resSelected?.token?.serializedToken;
      })
    );
  }

  // Authorize user playback
  private checkAuthorize(resource: string): Observable<AuthorizedResourcesResponse> {
    const authHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'AP-Device-Identifier': `fingerprint ${this.deviceHandler.getXDeviceFingerprint(this.clientRegistration.client_id)}`,
    });
    const body = { resources: [resource] };
    const mvpdName = this.profileSelected?.attributes?.mvpd?.value || '';
    return this.http.post<AuthorizedResourcesResponse>(`${this.apiUrl}/api/v2/univision/decisions/authorize/${mvpdName}`, body, {
      headers: authHeaders,
    });
  }

  // Validate user authorized resources packages
  private getPreauthorize(profToAuth: AdobeProfile): Observable<PreauthorizedResourcesResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'AP-Device-Identifier': `fingerprint ${this.deviceHandler.getXDeviceFingerprint(this.clientRegistration.client_id)}`,
    });
    const body = { resources: this.preAuthorizedResources };
    const mvpdName = profToAuth?.attributes?.mvpd ? profToAuth.attributes.mvpd.value : '';

    return this.http.post<PreauthorizedResourcesResponse>(`${this.apiUrl}/api/v2/univision/decisions/preauthorize/${mvpdName}`, body, {
      headers,
    });
  }

  // Get client id and client secret
  private register(): Observable<ClientRegistration> {
    const body = { software_statement: this.softwareStatement };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post<ClientRegistration>(`${this.apiUrl}/o/client/register`, body, { headers });
  }

  // Get client acccess token
  private getToken(): Observable<ClientToken> {
    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');
    body.set('client_id', this.clientRegistration.client_id);
    body.set('client_secret', this.clientRegistration.client_secret);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
    });

    return this.http.post<ClientToken>(`${this.apiUrl}/o/client/token`, body.toString(), { headers });
  }

  // Get device activation code
  private getRegCode(): Observable<SessionsResponse> {
    const body = new URLSearchParams();
    // body.set('redirectUrl', this.requestorId);
    // body.set('domainName', this.deviceHandler.getCurrentDeviceID());
    // body.set('mvpd', 'json');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.clientToken.access_token}`,
      'X-Device-Info': this.deviceHandler.getXDeviceInfo(),
      'Content-Type': 'application/x-www-form-urlencoded',
      'AP-Device-Identifier': `fingerprint ${this.deviceHandler.getXDeviceFingerprint(this.clientRegistration.client_id)}`,
    });

    return this.http.post<SessionsResponse>(`${this.apiUrl}/api/v2/univision/sessions`, body.toString(), { headers });
  }

  /**
   * Refresh token
   */
  private refreshToken(): void {
    const getToken: Subscription = this.getToken()
      .pipe(
        tap((tokenResponse) => {
          this.clientToken = tokenResponse;
          this.localStorage.setItem(LocalStorageKeys.PROFILE_TOKEN, JSON.stringify(tokenResponse));
          this.localStorage.setItem(
            LocalStorageKeys.PROFILE_TOKEN_EXP,
            this.calcExpDate(tokenResponse.expires_in, tokenResponse.created_at)
          );
          this.startRefreshTokenTimer();
        })
      )
      .subscribe(); // This ensures the request is actually made
    this.subscriptionHandlerService.addSubscription(this.serviceName, 'getToken', getToken);
  }

  private startRefreshTokenTimer(): void {
    try {
      this.stopRefreshTokenTimer();

      const timeUntilExpiryInSecs = this.clientToken?.expires_in ?? 0;
      const createdTimeInSecs = (this.clientToken?.created_at ?? 0) / 1000;
      const currentTimeInSecs = Date.now() / 1000;
      // Subtract delta time from current time vs created time
      const refreshTime = timeUntilExpiryInSecs - Math.abs(createdTimeInSecs - currentTimeInSecs);

      if (refreshTime <= 0) {
        this.refreshToken();
        return;
      }
      this.stopRefreshTokenTimer();
      this.refreshTokenTimeout = setTimeout(() => {
        this.refreshToken();
      }, refreshTime * 1000);
    } catch {}
  }

  // Calculate token expiration date based on expiresIn and createdAt values
  private calcExpDate(expiresInVal: number, createdAtVal: number): string {
    const createdDate = new Date(createdAtVal);
    createdDate.setSeconds(createdDate.getSeconds() + expiresInVal);
    return createdDate.getTime().toString();
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
  }
}
